import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

import {
  cleanConfig,
  compactLayout,
  removeItem,
  unusedIds,
} from "../../lib/dashboard/layout_ops"
import { resolveSectionBands } from "../../lib/dashboard/resolve_sections"
import { gridToLayout } from "../../lib/dashboard/serialize_layout"
import { preserveSeededSqcWidget } from "../../lib/dashboard/sqc_widget"
import { prefersReducedMotion } from "../../lib/prefers-reduced-motion"

// The edit-mode breakpoint MUST match the view-mode CSS breakpoint
// (`max-width: 999.98px` in application.css, CONEK-7425), so a widget dragged in
// while editing lands in the same breakpoint the view will render. If edit stays
// at a narrower width than view, a widget added at a tablet width is saved into
// the desktop array only and then vanishes on the next view render (its mobile
// entry is missing → `--m-display:none`). ">= 1000px" is desktop; below is mobile.
const DESKTOP_MEDIA_QUERY = "(min-width: 1000px)"

// Edit-mode engine for the Hotwire dashboard grid (/v2/dashboard). Mirrors the
// React DashboardProvider 1:1 while delegating drag/resize to GridStack. GridStack
// (+ its CSS) is lazy-loaded on the first edit so the read-only dashboard doesn't
// pay for it. In view mode the grid renders via CSS grid (`data-mode="view"`);
// entering edit hands the DOM to GridStack and it stays a disabled GridStack after
// exit (never reverts to CSS grid).
//
// Markup contract (rendered by the ERB, coded against — not the live DOM):
//   root:        data-controller + config/defaultConfig/updateUrl/editing values
//   grid target: `.grid-stack.dashboard-grid`, starts `data-mode="view"`; each
//                item is `.grid-stack-item.dashboard-widget` with gs-id/x/y/w/h/
//                min-w/min-h (DESKTOP coords) + data-widget-id, content in
//                `.grid-stack-item-content.dashboard-widget-cell`, remove button
//                inside the cell wired to `#removeWidget`.
//   widgetStore: `<template>` holding one `<div data-widget-id>` per UNUSED
//                widget, whose children are the full cell contents (incl. the
//                remove button). A widget's live content lives in exactly one
//                place — the store when unused, the grid when placed — and moves
//                between them on drop/remove.
//   previewCard: draggable source cards, each with data-widget-id, gs-w, gs-h,
//                data-min-w, data-min-h.
//   toasts:      `<template>` targets cloned into the global `#toast-container`.
export default class extends Controller {
  static values = {
    config: Object,
    defaultConfig: Object,
    updateUrl: String,
    editing: { type: Boolean, default: false },
  }

  static targets = [
    "grid",
    "frame",
    "previewArea",
    "previewCard",
    "widgetStore",
    "editButton",
    "editControls",
    "allUsedMessage",
    "savedToast",
    "errorToast",
    "resetToast",
  ]

  #gridStack = null
  #mediaQuery = null
  #mediaListener = null
  #beforeCacheHandler = null
  #suppressChange = false
  #editLabel = ""
  #doneLabel = ""
  #originalConfig = { desktop: [], mobile: [] }

  // Custom drag-in state. GridStack's own setupDragIn never fires `dropped` for
  // mouse (it only synthesizes the grid's mouseenter for touch), so we run the
  // drag-in ourselves: a floating helper follows the pointer and the drop is
  // resolved from the cursor position over the grid. Click stays the primary
  // add path; drag shares the same insert logic (#placeWidget).
  #dragCard = null
  #dragHelper = null
  #dragOffsetX = 0
  #dragOffsetY = 0
  #dragStartX = 0
  #dragStartY = 0
  #dragging = false
  #didDrag = false
  #pointerMoveHandler = null
  #pointerUpHandler = null

  connect() {
    this.gridInstance = null
    this.#originalConfig = this.#clone(this.configValue)
    this.workingConfig = this.#clone(this.configValue)
    this.currentBreakpoint = this.#detectBreakpoint()

    // The whole edit surface (edit/reset/clean buttons, preview area, widget
    // store, toasts) is omitted server-side without `dashboard.update`. Read it
    // only when present so the controller connects clean in pure read-only mode.
    if (this.hasEditButtonTarget) {
      this.#editLabel = this.editButtonTarget.textContent.trim()
      this.#doneLabel =
        this.editButtonTarget.dataset.doneLabel || this.#editLabel
    }

    this.#beforeCacheHandler = () => this.#exitEditWithoutSaving()
    document.addEventListener("turbo:before-cache", this.#beforeCacheHandler)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.#beforeCacheHandler)
    this.#removeMediaListener()
    this.#endPreviewDrag()
    if (this.gridInstance) {
      this.gridInstance.destroy(false)
      this.gridInstance = null
    }
  }

  // ─── Actions (wired in the ERB) ─────────────────────────────────────────────

  async toggleEdit() {
    if (this.editingValue) {
      await this.#save()
    } else {
      await this.#enterEdit()
    }
  }

  reset() {
    if (!this.gridInstance) return
    this.workingConfig = this.#clone(this.defaultConfigValue)
    this.#syncGridToLayout(this.workingConfig[this.currentBreakpoint])
    this.#refreshPreviewVisibility()
    this.#showToast(this.resetToastTarget)
  }

  clean() {
    if (!this.gridInstance) return
    // Empty both breakpoints, then keep a gate-off seeded SQC widget alive so a
    // clean never permanently drops it (CONEK-4938).
    this.workingConfig = preserveSeededSqcWidget(
      cleanConfig(),
      this.#originalConfig,
      new Set(this.#registeredIds()),
    )
    this.#syncGridToLayout(this.workingConfig[this.currentBreakpoint])
    this.#refreshPreviewVisibility()
  }

  removeWidget(event) {
    const item = event.currentTarget.closest(".grid-stack-item")
    if (!item || !this.gridInstance) return
    const widgetId = item.dataset.widgetId

    this.#suppressChange = true
    this.#returnNodeToStore(item)
    this.gridInstance.removeWidget(item, true, false)
    this.#suppressChange = false

    // A trashed widget leaves BOTH breakpoints.
    this.workingConfig = removeItem(this.workingConfig, widgetId)
    // Removing a header re-bands (or un-bands) the widgets that inherited it, so
    // repaint rather than leave a now-stale accent behind. CONEK-6788.
    this.#resolveSectionColours()
    this.#refreshPreviewVisibility()
  }

  // Click-to-add from the available-widgets drawer. GridStack auto-positions the
  // widget in the first free slot. A drag that just ended also emits a trailing
  // click on some browsers, so swallow it (the drag already placed the widget).
  addWidget(event) {
    if (this.#didDrag) {
      this.#didDrag = false
      return
    }
    this.#placeWidget(event.currentTarget, { autoPosition: true })
  }

  // ─── Custom drag-in (preview card → grid) ───────────────────────────────────

  // Pointerdown on a preview card. We track the gesture ourselves: below the move
  // threshold it stays a click (addWidget handles it); past it we drag a floating
  // helper and drop by cursor position. Wired in the ERB alongside the click.
  startPreviewDrag(event) {
    if (event.button !== 0 || !this.gridInstance) return
    const card = event.currentTarget
    if (!card.dataset.widgetId || this.#gridItemFor(card.dataset.widgetId))
      return

    this.#dragCard = card
    this.#dragStartX = event.clientX
    this.#dragStartY = event.clientY
    this.#dragging = false
    this.#didDrag = false
    this.#pointerMoveHandler = (e) => this.#onPreviewPointerMove(e)
    this.#pointerUpHandler = (e) => this.#onPreviewPointerUp(e)
    document.addEventListener("pointermove", this.#pointerMoveHandler)
    document.addEventListener("pointerup", this.#pointerUpHandler)
  }

  #onPreviewPointerMove(event) {
    if (!this.#dragging) {
      const moved =
        Math.abs(event.clientX - this.#dragStartX) +
        Math.abs(event.clientY - this.#dragStartY)
      if (moved <= 5) return
      this.#dragging = true
      this.#didDrag = true
      this.#createDragHelper(event)
    }
    this.#positionDragHelper(event)
  }

  #onPreviewPointerUp(event) {
    const card = this.#dragCard
    const wasDragging = this.#dragging
    this.#endPreviewDrag()
    if (!wasDragging || !card) return // a plain click — addWidget takes it

    // Resolve the drop against the grid: only place it if released over the grid,
    // at the cell under the cursor (parity with GridStack's own drop placement).
    const rect = this.gridTarget.getBoundingClientRect()
    const overGrid =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    if (!overGrid) return

    // pageX/pageY (document-relative) with useDocRelative so a scrolled page maps
    // to the right cell.
    const cell = this.gridInstance.getCellFromPixel(
      { left: event.pageX, top: event.pageY },
      true,
    )
    this.#placeWidget(card, { x: cell.x, y: cell.y, autoPosition: false })
  }

  // Tears down an in-flight drag: removes the document listeners and the floating
  // helper. Safe to call when no drag is active (disconnect, plain click).
  #endPreviewDrag() {
    if (this.#pointerMoveHandler) {
      document.removeEventListener("pointermove", this.#pointerMoveHandler)
      this.#pointerMoveHandler = null
    }
    if (this.#pointerUpHandler) {
      document.removeEventListener("pointerup", this.#pointerUpHandler)
      this.#pointerUpHandler = null
    }
    if (this.#dragHelper) {
      this.#dragHelper.remove()
      this.#dragHelper = null
    }
    this.#dragCard = null
    this.#dragging = false
  }

  #createDragHelper(event) {
    const rect = this.#dragCard.getBoundingClientRect()
    this.#dragOffsetX = event.clientX - rect.left
    this.#dragOffsetY = event.clientY - rect.top

    const helper = this.#dragCard.cloneNode(true)
    helper.removeAttribute("data-testid")
    helper.classList.remove("hidden")
    Object.assign(helper.style, {
      position: "fixed",
      width: `${rect.width}px`,
      margin: "0",
      pointerEvents: "none",
      opacity: "0.85",
      zIndex: "10000",
    })
    document.body.appendChild(helper)
    this.#dragHelper = helper
    this.#positionDragHelper(event)
  }

  #positionDragHelper(event) {
    if (!this.#dragHelper) return
    this.#dragHelper.style.left = `${event.clientX - this.#dragOffsetX}px`
    this.#dragHelper.style.top = `${event.clientY - this.#dragOffsetY}px`
  }

  // Shared insert path for click (autoPosition) and drag-drop (explicit x/y).
  // Moves the widget's content out of the store into the grid, then reads the
  // resulting layout back into the active breakpoint. No-op if already placed.
  #placeWidget(card, { x, y, autoPosition }) {
    const widgetId = card.dataset.widgetId
    if (!widgetId || !this.gridInstance || this.#gridItemFor(widgetId)) return

    this.#suppressChange = true
    this.#insertWidget({
      i: widgetId,
      x,
      y,
      w: Number(card.getAttribute("gs-w")),
      h: Number(card.getAttribute("gs-h")),
      minW: Number(card.dataset.minW),
      minH: Number(card.dataset.minH),
      autoPosition,
    })
    this.#suppressChange = false

    // A add lands in the ACTIVE breakpoint only (parity with the previous drop).
    this.#syncActiveBreakpointFromGrid()
    // Dropping in a header (or a widget under one) changes the bands — repaint so
    // the accents follow immediately, not on the next unrelated change. CONEK-6788.
    this.#resolveSectionColours()
    this.#refreshPreviewVisibility()
  }

  // ─── Edit lifecycle ─────────────────────────────────────────────────────────

  async #enterEdit() {
    await this.#ensureGridStack()
    this.currentBreakpoint = this.#detectBreakpoint()

    // Hand the DOM to GridStack: drop CSS-grid view mode on the grid, mark the
    // frame as editing.
    this.gridTarget.removeAttribute("data-mode")
    this.frameTarget.dataset.mode = "edit"

    if (!this.gridInstance) {
      this.gridInstance = this.#gridStack.init(
        {
          cellHeight: 46,
          margin: 8,
          column: 6,
          float: false,
          minRow: 1,
          animate: !prefersReducedMotion(),
          acceptWidgets: true,
        },
        this.gridTarget,
      )
      this.gridInstance.on("change", () => this.#onChange())
      // Drag-in from the drawer is handled by our own pointer flow
      // (startPreviewDrag), not GridStack's setupDragIn — see the drag-in note on
      // the state fields above.
    } else {
      this.gridInstance.enable()
    }

    // Reconcile to the active breakpoint (column count + positions). On the first
    // desktop init this is a no-op; it matters when opening in mobile or when the
    // viewport changed while out of edit.
    this.#suppressChange = true
    this.gridInstance.column(this.currentBreakpoint === "mobile" ? 2 : 6)
    this.#suppressChange = false
    this.#syncGridToLayout(this.workingConfig[this.currentBreakpoint])

    this.#addMediaListener()
    this.#setEditingUi(true)
    this.#refreshPreviewVisibility()
  }

  #exitEdit() {
    // Keep GridStack around but disabled — do NOT revert to CSS grid.
    if (this.gridInstance) this.gridInstance.disable()
    this.frameTarget.dataset.mode = "view"
    this.#removeMediaListener()
    this.#setEditingUi(false)
  }

  #exitEditWithoutSaving() {
    if (!this.editingValue) return
    this.#exitEdit()
  }

  async #save() {
    if (this.hasEditButtonTarget) this.editButtonTarget.disabled = true

    // Re-inject a gate-off seeded SQC widget that was filtered out of the grid so
    // saving never drops it (CONEK-4938). No-op when the gate is on or the tenant
    // never had it seeded.
    const preserved = preserveSeededSqcWidget(
      this.workingConfig,
      this.#originalConfig,
      new Set(this.#registeredIds()),
    )
    // Persist the top-gap compaction the user sees in edit mode. GridStack's
    // float:false gravity closes the empty band above the first widget on screen,
    // but that rebuild runs suppressed (#syncGridToLayout) so workingConfig keeps
    // the stale coordinates; normalise here so "Edit → Done" actually sticks and
    // the gap does not reappear on the next view render (CONEK-5978).
    const payload = compactLayout(preserved)

    let ok = false
    try {
      const response = await patch(this.updateUrlValue, {
        body: { dashboard: { config: payload } },
        contentType: "application/json",
        responseKind: "json",
      })
      ok = response.ok
    } catch {
      ok = false
    }

    if (this.hasEditButtonTarget) this.editButtonTarget.disabled = false

    if (ok) {
      this.workingConfig = payload
      this.#originalConfig = this.#clone(payload)
      this.configValue = payload
    }

    this.#exitEdit()
    this.#showToast(ok ? this.savedToastTarget : this.errorToastTarget)
  }

  // ─── GridStack event handlers ───────────────────────────────────────────────

  #onChange() {
    if (this.#suppressChange) return
    this.#resolveSectionColours()
    this.#syncActiveBreakpointFromGrid()
    this.#refreshPreviewVisibility()
  }

  // Live re-derivation of each widget's section band from the header above it
  // (CONEK-6788), so the accent follows a widget as it is dragged between bands.
  // Visual only: it writes data-section (what the CSS reads), never
  // data-section-explicit (what gridToLayout persists), so inheritance stays
  // dynamic. An explicit override, and a widget above the first header, are left
  // as the server resolved them.
  #resolveSectionColours() {
    const nodes = [...(this.gridInstance?.engine?.nodes ?? [])]
      .filter((node) => node.el)
      .sort((a, b) => a.y - b.y || a.x - b.x)

    // Derivation is a shared pure function (resolveSectionBands) so it stays
    // unit-tested and in lock-step with the server's GridComponent#inherited_bands.
    const bands = resolveSectionBands(
      nodes.map((node) => ({
        id: node.el.dataset.widgetId ?? "",
        sectionExplicit: node.el.dataset.sectionExplicit,
      })),
    )
    nodes.forEach((node, i) => {
      const section = bands[i].section
      if (section) node.el.dataset.section = section
      else delete node.el.dataset.section
    })
  }

  // ─── DOM ⇄ layout reconciliation ────────────────────────────────────────────

  #syncActiveBreakpointFromGrid() {
    this.workingConfig = {
      ...this.workingConfig,
      [this.currentBreakpoint]: gridToLayout(this.gridInstance),
    }
  }

  // Reconciles the grid DOM to `items` (the active breakpoint's layout): removes
  // widgets no longer present (returning their content to the store), repositions
  // survivors, and adds new ones from the store. Batched so it fires a single
  // change and is suppressed so it doesn't clobber `workingConfig`.
  #syncGridToLayout(items) {
    const grid = this.gridInstance
    this.#suppressChange = true
    grid.batchUpdate()

    const desiredIds = new Set(items.map((item) => item.i))
    ;[...(grid.engine.nodes ?? [])].forEach((node) => {
      if (!desiredIds.has(node.id)) {
        this.#returnNodeToStore(node.el)
        grid.removeWidget(node.el, true, false)
      }
    })

    items.forEach((item) => {
      const existing = this.#gridItemFor(item.i)
      if (existing) {
        grid.update(existing, {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          minW: item.minW,
          minH: item.minH,
        })
      } else {
        this.#insertWidget(item)
      }
    })

    grid.batchUpdate(false)
    this.#suppressChange = false
    // This whole rebuild runs suppressed, so the "change" it fires never reaches
    // #onChange — repaint the section accents here so reset / clean / breakpoint
    // switch land with every band correctly tinted. CONEK-6788.
    this.#resolveSectionColours()
  }

  #insertWidget(item) {
    const el = this.#buildWidgetElement(item.i)
    if (!el) return
    this.gridTarget.appendChild(el)
    this.gridInstance.makeWidget(el, {
      id: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      minW: item.minW,
      minH: item.minH,
      // Click-add passes autoPosition (no x/y); drop/sync pass explicit coords
      // and leave this undefined so gridstack honours them.
      autoPosition: item.autoPosition,
    })
  }

  // Builds a `.grid-stack-item` wrapper and moves the widget's live content out of
  // the store into its cell. Returns null when the store has no content for the id
  // (e.g. a gate-off SQC widget that is preserved in config but never rendered).
  #buildWidgetElement(widgetId) {
    const stored = this.#takeFromStore(widgetId)
    if (!stored) return null

    const item = document.createElement("div")
    item.className = "grid-stack-item dashboard-widget"
    item.dataset.widgetId = widgetId
    // Match the server-rendered items' identifying attribute so a widget dragged
    // back in or restored by reset is found by `[data-testid="widget-<id>"]`.
    item.dataset.testid = `widget-${widgetId}`
    // Section-header pseudo-widget (CONEK-6788): flag it so the section CSS strips
    // the card chrome and tints the divider even mid-edit — the store content is
    // server-rendered but this wrapper is built here. The band comes from the id,
    // and data-section is what gridToLayout reads back to persist it.
    const headerPrefix = "SectionHeader:"
    if (widgetId.startsWith(headerPrefix)) {
      item.dataset.sectionHeader = ""
      item.dataset.section = widgetId.slice(headerPrefix.length)
    }
    // Preserve an explicit per-item section override across a remove/re-add or
    // breakpoint switch — it's the only section value that is persisted; the
    // derived data-section is repainted by #resolveSectionColours. CONEK-6788.
    if (stored.dataset.sectionExplicit) {
      item.dataset.sectionExplicit = stored.dataset.sectionExplicit
    }

    const cell = document.createElement("div")
    cell.className = "grid-stack-item-content dashboard-widget-cell"
    cell.append(...stored.childNodes)
    item.appendChild(cell)
    return item
  }

  #takeFromStore(widgetId) {
    const stored = this.widgetStoreTarget.content.querySelector(
      `[data-widget-id="${CSS.escape(widgetId)}"]`,
    )
    if (stored) stored.remove()
    return stored
  }

  #returnNodeToStore(el) {
    if (!el) return
    const widgetId = el.dataset.widgetId
    const cell = el.querySelector(".dashboard-widget-cell")
    const stored = document.createElement("div")
    stored.dataset.widgetId = widgetId
    // Carry the explicit section override across the store round-trip so a
    // remove/re-add (or breakpoint switch) doesn't silently drop it. CONEK-6788.
    if (el.dataset.sectionExplicit) {
      stored.dataset.sectionExplicit = el.dataset.sectionExplicit
    }
    if (cell) stored.append(...cell.childNodes)
    this.widgetStoreTarget.content.appendChild(stored)
  }

  #gridItemFor(widgetId) {
    return this.gridTarget.querySelector(
      `:scope > .grid-stack-item[data-widget-id="${CSS.escape(widgetId)}"]`,
    )
  }

  // ─── Preview cards / breakpoint / UI ────────────────────────────────────────

  #refreshPreviewVisibility() {
    const unused = new Set(
      unusedIds(
        this.workingConfig,
        this.currentBreakpoint,
        this.#registeredIds(),
      ),
    )
    this.previewCardTargets.forEach((card) => {
      this.#setHidden(card, !unused.has(card.dataset.widgetId))
    })
    if (this.hasAllUsedMessageTarget) {
      this.#setHidden(this.allUsedMessageTarget, unused.size > 0)
    }
  }

  #registeredIds() {
    return this.previewCardTargets.map((card) => card.dataset.widgetId)
  }

  #addMediaListener() {
    if (this.#mediaListener) return
    this.#mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY)
    this.#mediaListener = () => this.#onBreakpointChange()
    this.#mediaQuery.addEventListener("change", this.#mediaListener)
  }

  #removeMediaListener() {
    if (this.#mediaQuery && this.#mediaListener) {
      this.#mediaQuery.removeEventListener("change", this.#mediaListener)
    }
    this.#mediaListener = null
  }

  #onBreakpointChange() {
    if (!this.gridInstance || !this.editingValue) return
    // Persist the breakpoint we're leaving before switching.
    this.#syncActiveBreakpointFromGrid()
    const next = this.#detectBreakpoint()
    if (next === this.currentBreakpoint) return

    this.currentBreakpoint = next
    this.#suppressChange = true
    this.gridInstance.column(next === "mobile" ? 2 : 6)
    this.#suppressChange = false
    this.#syncGridToLayout(this.workingConfig[next])
    this.#refreshPreviewVisibility()
  }

  #detectBreakpoint() {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches ? "desktop" : "mobile"
  }

  #setEditingUi(editing) {
    this.editingValue = editing
    if (this.hasEditButtonTarget) {
      this.editButtonTarget.textContent = editing
        ? this.#doneLabel
        : this.#editLabel
      // Fill while editing ("Done"), outline at rest ("Edit"). The fill classes
      // key off data-active in the markup — flip only the attribute here.
      this.editButtonTarget.dataset.active = editing ? "true" : "false"
    }
    if (this.hasPreviewAreaTarget)
      this.#setHidden(this.previewAreaTarget, !editing)
    // Reset/Clean share one wrapper (editControls) — see the ERB comment on
    // why the wrapper, not each button, carries the hidden toggle.
    if (this.hasEditControlsTarget)
      this.#setHidden(this.editControlsTarget, !editing)
  }

  #setHidden(el, hidden) {
    el.classList.toggle("hidden", hidden)
  }

  #showToast(templateTarget) {
    if (!templateTarget) return
    const container = document.getElementById("toast-container")
    if (!container) return
    container.appendChild(templateTarget.content.cloneNode(true))
  }

  async #ensureGridStack() {
    if (this.#gridStack) return
    const module = await import("gridstack")
    await import("gridstack/dist/gridstack.min.css")
    this.#gridStack = module.GridStack
  }

  #clone(value) {
    return JSON.parse(JSON.stringify(value ?? { desktop: [], mobile: [] }))
  }
}
